/* eslint-disable prettier/prettier */
/* eslint-disable no-console */
// utils/calculateVentas.js

/**
 * ============================================================================
 * UTILIDAD CONSOLIDADA PARA CÁLCULO DE VENTAS
 * ============================================================================
 *
 * Calcula cierres y facturación para todas las verticales:
 * - Publicidad (PAUTA)
 * - Hardware (CAPEX/OPEX)
 * - Gestión & Contenidos (Cartelería)
 * - BTL
 *
 * Tipos de cambio delegados a exchangeRateService.js (fuente centralizada)
 *
 * USO: ER, VentasTabla, VentasDetalle, VentasProyeccion, etc.
 */

import { calendarioNegocio } from "./carteleria/calendarioNegocio";
import { convertirMontoCarteleriaPorFecha } from "./carteleria/convertirMontoCarteleria";
import { combinarPorCorte, esMesHistorico } from "./carteleria/corteHistorico";
import {
  calcularHardwareMensual as calcularHardwareMensualHistorico,
  calcularGestionContenidosMensual as calcularGestionContenidosMensualHistorico,
} from "./carteleria/historico/calculateVentasHistorico";
import {
  loadExchangeRates,
  getUFValue as _getUFValue,
  getDolarValue as _getDolarValue,
  convertirAMonedaLocal as _convertirAMonedaLocal,
} from "./exchangeRateService";

// Re-exportar para compatibilidad con importadores existentes
export const getUFValue = _getUFValue;
export const getDolarValue = _getDolarValue;
export const convertirAMonedaLocal = _convertirAMonedaLocal;

// ============================================================================
// 2. FUNCIONES LEGACY (mantener compatibilidad)
// ============================================================================

export const getConvertedImporte = (oportunidad, rate) =>
  oportunidad.moneda_dolar ? (oportunidad.importe || 0) * rate : oportunidad.importe || 0;

export const getConvertedImporteBTL = (oportunidadBTL, rate) =>
  oportunidadBTL.moneda_dolar ? (oportunidadBTL.total_investment || 0) * rate : oportunidadBTL.total_investment || 0;

/**
 * Determina la tasa de cambio correcta para una oportunidad/contrato.
 * Prioridad: facturafecha → fecha_cierre → mes_cierre → fallback.
 * @param {object} opp - Oportunidad con moneda_dolar, facturafecha, fecha_cierre, mes_cierre
 * @param {function} getDolarValueFn - Función (fechaStr) => number (ya bound al país)
 * @param {number} [fallback=1] - Valor fallback si no hay fecha disponible
 */
export const getRateForOpp = (opp, getDolarValueFn, fallback = 1) => {
  if (!opp.moneda_dolar) return 1;
  if (opp.facturafecha) return getDolarValueFn(opp.facturafecha);
  if (opp.fecha_cierre) return getDolarValueFn(opp.fecha_cierre);
  if (opp.mes_cierre) {
    const [mes, ano] = opp.mes_cierre.split("-");
    if (mes && ano) return getDolarValueFn(`${ano}-${mes}-15`);
  }
  return fallback;
};

/**
 * Convierte el importe de una oportunidad a USD usando rate histórico por fecha.
 * Si la oportunidad ya está en USD (moneda_dolar=true), retorna el importe sin tocar.
 * Caso contrario, divide por el dólar de la fecha relevante.
 * Misma cascada de fechas que getRateForOpp: facturafecha → fecha_cierre → mes_cierre → fallback.
 * @param {object} opp - Oportunidad con moneda_dolar, importe/total_investment, fechas
 * @param {function} getDolarValueFn - Función (fechaStr) => number (ya bound al país)
 * @param {number} [fallback=1] - Rate fallback si no hay fecha disponible
 */
export const getImporteEnUSD = (opp, getDolarValueFn, fallback = 1) => {
  const importe = Number(opp.importe || opp.total_investment || 0);
  if (opp.moneda_dolar) return importe;
  let rate = fallback;
  if (opp.facturafecha) rate = getDolarValueFn(opp.facturafecha) || fallback;
  else if (opp.fecha_cierre) rate = getDolarValueFn(opp.fecha_cierre) || fallback;
  else if (opp.mes_cierre) {
    const [mes, ano] = opp.mes_cierre.split("-");
    if (mes && ano) rate = getDolarValueFn(`${ano}-${mes}-15`) || fallback;
  }
  return rate > 0 ? importe / rate : 0;
};

export const normalizeMonth = (month, createdAt, ano = null) => {
  if (!month || typeof month !== "string") {
    if (ano) return `01-${ano}`;
    return null;
  }
  if (/^\d{2}-\d{4}$/.test(month)) return month;
  const monthNum = parseInt(month, 10);
  if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12 && ano) {
    return `${monthNum.toString().padStart(2, "0")}-${ano}`;
  }
  return null;
};

/**
 * Suma el importe (ya convertido) de TODAS las oportunidades de un array, sin deduplicar.
 * Primitivo genérico — usado directo para el estatus 100% (cerrado, cada fila es venta real) y
 * como paso final de `sumarImportesPorEstatus` para los estatus abiertos, una vez reducidos a
 * uno por cliente.
 * @param {Array} ops - oportunidades a sumar.
 * @param {(op: object) => number} getValor - value-getter (ya resuelve moneda/USD/local).
 * @returns {number} suma total, sin dedup.
 */
export const sumarImportes = (ops, getValor) => ops.reduce((sum, op) => sum + (Number(getValor(op)) || 0), 0);

// Estatus donde aplica "Proyección de pipeline por estatus" (regla 10.272): el 100% queda afuera
// a propósito, ahí no hay alternativas, es venta cerrada y se suma todo.
const ESTATUS_ABIERTOS_PIPELINE_DEDUP = ["90%", "75%", "50%", "0%"];

/**
 * Suma el importe de oportunidades de UN estatus aplicando "Proyección de pipeline por
 * estatus" (regla 10.272, docs/reglas/universales.md#r10-272): en estatus ABIERTOS
 * (90%/75%/50%/0%), varias propuestas del MISMO cliente en el MISMO estatus son alternativas de
 * una misma campaña — el cliente va a comprar una sola. La proyección cuenta una sola por
 * cliente y estatus, y por criterio conservador, la de MENOR monto. Al 100% no aplica: lo
 * cerrado se suma todo (cada fila es venta real, no una alternativa).
 *
 * Fuente única para las tarjetas de estatus/barra de % en VentasDetalle, VentasMiListaClientesV2
 * (CRM) y VentasMiListaClientes (legacy) — 10.1: antes existían 3 copias sin documentar de esta
 * misma regla; una sesión la tomó por bug (10.229 mal aplicado) y la sacó, y el 75% de CENCOSUD
 * pasó de $7.325.941 a $26.771.456. La tabla de detalle sigue listando TODAS las oportunidades
 * (acá no se filtra nada) — la tarjeta no tiene por qué coincidir con la suma de la tabla, es
 * una transformación declarada del mismo array/predicado, no un predicado distinto (10.229 se
 * sigue cumpliendo: misma fuente, misma fila de origen).
 * @param {Array} ops - oportunidades de UN estatus (ya filtradas por estatus/fecha/país/etc).
 * @param {(op: object) => number} getValor - value-getter (ya resuelve moneda/USD/local/descuento).
 * @param {string} estatus - "100%"|"90%"|"75%"|"50%"|"0%" — decide si aplica el mínimo por cliente.
 * @returns {number}
 */
export const sumarImportesPorEstatus = (ops, getValor, estatus) => {
  if (!ESTATUS_ABIERTOS_PIPELINE_DEDUP.includes(estatus)) return sumarImportes(ops, getValor);

  const minPorCliente = new Map();
  ops.forEach((op) => {
    const clientId = op.client_id || op.cliente;
    const importe = Number(getValor(op)) || 0;
    if (!minPorCliente.has(clientId) || importe < minPorCliente.get(clientId)) {
      minPorCliente.set(clientId, importe);
    }
  });
  return sumarImportes([...minPorCliente.values()], (v) => v);
};

/**
 * Devuelve las OPORTUNIDADES ganadoras por estatus (regla 10.272), mismo criterio que
 * `sumarImportesPorEstatus` (que sigue intacta — esta función es su hermana, no la
 * reemplaza) pero retornando los objetos seleccionados en vez del total: en un estatus
 * ABIERTO (90/75/50/0%), una sola por cliente — la de MENOR importe (las demás son
 * alternativas de la misma campaña); al 100% se devuelven TODAS (cada fila es venta real).
 * Necesaria para superficies que necesitan renderizar la oportunidad ganadora (nombre,
 * campaña, agencia), no solo su total — caso: etiquetas de cliente de la Pizarra.
 * @param {Array} ops - oportunidades de UN estatus (ya filtradas por estatus/fecha/país/etc).
 * @param {(op: object) => number} getValor - value-getter (ya resuelve moneda/USD/local/descuento).
 * @param {string} estatus - "100%"|"90%"|"75%"|"50%"|"0%" — decide si aplica el mínimo por cliente.
 * @returns {Array} oportunidades seleccionadas (una por cliente en estatus abiertos, todas al 100%).
 */
export const seleccionarOportunidadesPorEstatus = (ops, getValor, estatus) => {
  if (!ESTATUS_ABIERTOS_PIPELINE_DEDUP.includes(estatus)) return ops;

  const winnerPorCliente = new Map();
  ops.forEach((op) => {
    const clientId = op.client_id || op.cliente;
    const importe = Number(getValor(op)) || 0;
    const actual = winnerPorCliente.get(clientId);
    if (!actual || importe < (Number(getValor(actual)) || 0)) {
      winnerPorCliente.set(clientId, op);
    }
  });
  return [...winnerPorCliente.values()];
};

export const calculateSalesSums = (
  oportunidades,
  oportunidadesBTL,
  year,
  startMonth = 1,
  endMonth = 12,
  rate,
  pais
) => {
  const filterAndSum = (ops, status, field, isBTL = false) => {
    const filteredOps = ops.filter((op) => {
      let effectiveMonth, effectiveYear;

      // Para facturación: prioridad 1 = facturafecha, prioridad 2 = mes_factura
      if (field === "mes_factura") {
        if (op.facturafecha) {
          const facturaDate = new Date(op.facturafecha);
          if (!isNaN(facturaDate.getTime())) {
            effectiveMonth = facturaDate.getMonth() + 1;
            effectiveYear = facturaDate.getFullYear();
          }
        }
        if (!effectiveMonth && op.mes_factura) {
          const [mesStr, anoStr] = op.mes_factura.split("-").map(Number);
          effectiveMonth = mesStr;
          effectiveYear = anoStr;
        }
      } else {
        // Para cierres y otros: usar el campo directamente
        if (!op[field]) return false;
        const [mesStr, anoStr] = op[field].split("-").map(Number);
        effectiveMonth = mesStr;
        effectiveYear = anoStr;
      }

      // Si no hay fecha válida, descartar
      if (!effectiveMonth || !effectiveYear) return false;

      const estatus = isBTL ? (op.status === 100 ? "100%" : `${op.status || 0}%`) : op.estatus;
      return (
        estatus === status &&
        !op.eliminada &&
        effectiveYear === parseInt(year) &&
        effectiveMonth >= startMonth &&
        effectiveMonth <= endMonth &&
        op.pais === pais
      );
    });
    // Proyección de pipeline por estatus (10.272) — ver sumarImportesPorEstatus arriba.
    return sumarImportesPorEstatus(
      filteredOps,
      (op) => (isBTL ? getConvertedImporteBTL(op, rate) : getConvertedImporte(op, rate)),
      status
    );
  };

  const sumCierresRegular = filterAndSum(oportunidades, "100%", "mes_cierre", false);
  const sumCierresBTL = filterAndSum(oportunidadesBTL, "100%", "mes_cierre", true);
  const sumaImportes100Regular = filterAndSum(oportunidades, "100%", "mes_factura", false);
  const sumaImportes100BTL = filterAndSum(oportunidadesBTL, "100%", "mes_factura", true);
  const sumaImportes90 = filterAndSum(oportunidades, "90%", "mes_cierre", false);
  const sumaImportes75 = filterAndSum(oportunidades, "75%", "mes_cierre", false);
  const sumaImportes50 = filterAndSum(oportunidades, "50%", "mes_cierre", false);
  const sumaImportes0 = filterAndSum(oportunidades, "0%", "mes_cierre", false);

  return {
    sumCierres: sumCierresRegular + sumCierresBTL,
    sumaImportes100: sumaImportes100Regular + sumaImportes100BTL,
    sumaImportes90,
    sumaImportes75,
    sumaImportes50,
    sumaImportes0,
  };
};

export const calculateForecast = (sum90, sum75) => sum90 * 0.75 + sum75 * 0.5;

// ============================================================================
// 3. NUEVAS FUNCIONES: CÁLCULOS MENSUALES CONSOLIDADOS
// ============================================================================

/**
 * Calcula ingresos mensuales de PUBLICIDAD (facturación)
 * LÓGICA IDÉNTICA A ER: solo incluye oportunidades con facturafecha
 * @param {number} ano - Año a calcular
 * @param {Array} oportunidades - Array de oportunidades
 * @param {string} pais - "Chile" o "Perú"
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
export const calcularPublicidadMensual = async (ano, oportunidades, pais) => {
  await loadExchangeRates();

  const valores = Array(12).fill(0);

  oportunidades
    // FILTRO IGUAL QUE ER: solo con facturafecha (facturado real)
    .filter((opp) => !opp.eliminada && opp.facturafecha)
    .forEach((opp) => {
      const facturaDate = new Date(opp.facturafecha);
      if (isNaN(facturaDate.getTime())) return;

      const monthIndex = facturaDate.getMonth();
      const facturaYear = facturaDate.getFullYear();

      if (facturaYear === ano && monthIndex >= 0 && monthIndex < 12) {
        const importe = parseFloat(opp.importe) || 0;
        // Conversión USD → moneda local por FECHA de factura (dólar del día),
        // no por tasa única. Respeta la lógica histórica del proyecto.
        const valorLocal = opp.moneda_dolar
          ? _convertirAMonedaLocal(importe, "USD", pais, opp.facturafecha)
          : importe;
        valores[monthIndex] += valorLocal;
      }
    });

  return valores;
};

/**
 * Calcula ingresos mensuales de BTL (facturación)
 * LÓGICA IDÉNTICA A ER: solo incluye registros con facturafecha
 * @param {number} ano - Año a calcular
 * @param {Array} salesBTL - Array de ventas BTL
 * @param {string} pais - "Chile" o "Perú" (no usado, para compatibilidad)
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
// eslint-disable-next-line no-unused-vars
export const calcularBTLMensual = async (ano, salesBTL, pais) => {
  const mapa = Array(12).fill(0);

  salesBTL.forEach((row) => {
    // FILTRO IGUAL QUE ER: solo con facturafecha
    if (!row.facturafecha) return;

    const fecha = new Date(row.facturafecha);
    if (isNaN(fecha.getTime())) return;
    if (fecha.getFullYear() !== ano) return;

    const mes = fecha.getMonth();
    // ER usa directo total_investment sin conversión
    mapa[mes] += Number(row.total_investment) || 0;
  });

  return mapa;
};

// Fecha de facturación de un mes desde `negocios_carteleria.meses_facturacion` — MISMA fuente/fecha
// que BRUTO (useVFDataLoading: `mesFact.facturafecha`). `tipo`: "gestion" | "opex". Se usa para que la
// conversión UF/USD de Proyección caiga en la MISMA fecha que la grilla de Facturación cuando NO hay
// registro en costos_gestion_contenidos, en vez del viejo fallback a fin de mes (divergencia diagnosticada).
const getFacturafechaMesesFact = (negocio, tipo, mesIndex, ano) => {
  const mf =
    typeof negocio.meses_facturacion === "string"
      ? JSON.parse(negocio.meses_facturacion || "[]")
      : negocio.meses_facturacion || [];
  if (!Array.isArray(mf)) return null;
  const mesStr = String(mesIndex + 1).padStart(2, "0");
  const anoStr = String(ano);
  const rec = mf.find((x) => x && x.tipo === tipo && x.mes === mesStr && x.ano === anoStr);
  return rec?.facturafecha || null;
};

/**
 * Calcula ingresos mensuales de HARDWARE (facturación)
 * F3c (rediseño de Cartelería, "lo histórico no se toca"): los meses ANTERIORES al corte (2026-09 inclusive)
 * salen de la lógica histórica congelada (`./carteleria/historico/calculateVentasHistorico.js`, copia literal
 * del código anterior); los meses desde 2026-10 salen del motor `calendarioNegocio` (montos, tramos, ventana
 * del servicio), conservando las FECHAS de conversión UF/USD del código anterior.
 * @param {number} ano - Año a calcular
 * @param {Array} negociosCarteleria - Array de negocios cartelería
 * @param {string} pais - "Chile" o "Perú"
 * @param {Array} costosGestionData - Datos de costos gestión (para fechas factura OPEX)
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
export const calcularHardwareMensual = async (ano, negociosCarteleria, pais, costosGestionData = []) => {
  await loadExchangeRates();
  const hayHistorico = esMesHistorico(ano, 0);
  const hayMotor = !esMesHistorico(ano, 11);
  const historico = hayHistorico
    ? await calcularHardwareMensualHistorico(ano, negociosCarteleria, pais, costosGestionData)
    : null;
  if (!hayMotor) return historico;
  const motor = await calcularHardwareMensualMotor(ano, negociosCarteleria, pais, costosGestionData, new Date());
  return historico ? combinarPorCorte(ano, historico, motor) : motor;
};

// Ventana OPEX del código anterior = `fecha_cierre` + (num_cuotas || plazo || 36) meses. El motor la calcula
// desde `fecha_inicio` + plazo (con "el estado manda"). Solo se puede leer del motor cuando ambas arrancan en el
// mismo mes (por texto) y cuentan las mismas cuotas; si no, se sigue con la lógica anterior para ese negocio.
const ventanaOpexLeibleDelMotor = (n, cal) => {
  const plazo = parseInt(n.plazo_contrato_meses, 10) || 0;
  const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;
  return (
    plazo > 0 &&
    totalCuotas === plazo &&
    !!n.fecha_inicio &&
    String(n.fecha_cierre).slice(0, 7) === String(n.fecha_inicio).slice(0, 7) &&
    cal.devengo.some((d) => d.componente === "opex")
  );
};

const calcularHardwareMensualMotor = async (ano, negociosCarteleria, pais, costosGestionData, hoy) => {
  const mapa = Array(12).fill(0);

  for (const n of negociosCarteleria) {
    if (!n.incluye_hardware || n.eliminada) continue;
    if (!["100%", "Activo"].includes(n.estado)) continue;

    const moneda = (n.moneda_hardware || "UF").toUpperCase();
    const cal = calendarioNegocio(n, { hoy });
    const capex = cal.hardwareCapex;
    let yaProcesadoConDesglose = false;

    // === CAPEX CON DESGLOSE DE PAGOS === (mes de facturación del tramo; cascada de fechas del código
    // anterior). Monto = `montoEfectivo` del motor: D12, un tramo ÚNICO sin monto vale `monto_hardware`.
    const detalleCapex = Array.isArray(n.detalle_pagos_hardware) ? n.detalle_pagos_hardware : [];
    if (n.modalidad === "CAPEX" && detalleCapex.length > 0) {
      detalleCapex.forEach((pago, i) => {
        const fechaTramo = pago.facturafecha || pago.fecha_factura || pago.fecha_factura_estimada || n.fecha_cierre;
        const tramo = capex.tramos[i];
        const monto = tramo.montoEfectivo;
        // Un tramo con monto null se salteaba; ahora solo se saltea si el motor tampoco le da monto (D12).
        if (!fechaTramo || (pago.monto == null && !(monto > 0))) return;

        const fecha = new Date(fechaTramo);
        if (fecha.getFullYear() !== ano) return;

        mapa[fecha.getMonth()] += convertirMontoCarteleriaPorFecha(monto, moneda, pais, fechaTramo.split("T")[0]);
      });
      yaProcesadoConDesglose = true;
    }

    // === OTROS CAPEX (instalación, etc.) ===
    if (n.modalidad === "CAPEX" && Array.isArray(n.detalle_otros_hardware) && n.detalle_otros_hardware.length > 0) {
      n.detalle_otros_hardware.forEach((pago, i) => {
        if (!pago.fecha_factura || pago.monto == null) return;

        const fecha = new Date(pago.fecha_factura);
        if (fecha.getFullYear() !== ano) return;

        const monto = capex.otros[i].monto;
        mapa[fecha.getMonth()] += convertirMontoCarteleriaPorFecha(monto, moneda, pais, pago.fecha_factura.split("T")[0]);
      });
    }

    // === CAPEX SIN DESGLOSE ===
    if (n.modalidad === "CAPEX" && n.fecha_cierre && !yaProcesadoConDesglose) {
      const fechaCierre = new Date(n.fecha_cierre);
      const mesCierre = fechaCierre.getMonth();
      if (fechaCierre.getFullYear() !== ano) continue;

      const monto = capex.monto;
      const fechaStr = new Date(ano, mesCierre + 1, 0).toISOString().split("T")[0];

      mapa[mesCierre] += convertirMontoCarteleriaPorFecha(monto, moneda, pais, fechaStr);
    }

    // === OPEX (cuotas mensuales) === ventana y cuota del motor (devengo `opex`): el servicio termina en su
    // último mes (fin de contrato) y un activo vencido sigue hasta el mes actual (el estado manda).
    else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
      if (!ventanaOpexLeibleDelMotor(n, cal)) {
        const h = await calcularHardwareMensualHistorico(ano, [n], pais, costosGestionData);
        h.forEach((v, m) => {
          mapa[m] += v;
        });
        continue;
      }
      cal.devengo
        .filter((d) => d.componente === "opex" && d.ano === ano)
        .forEach((d) => {
          const mesActual = d.mesNum - 1;
          // Fecha para convertir UF/USD (misma prioridad que el código anterior).
          const registro = costosGestionData.find(
            (c) =>
              Number(c.negocio_id) === n.id &&
              Number(c.mes) === mesActual + 1 &&
              Number(c.ano) === ano &&
              c.fecha_factura
          );
          const primerDiaMes = `${ano}-${String(mesActual + 1).padStart(2, "0")}-01`;
          const fechaFactura =
            registro?.fecha_factura?.split("T")[0] || getFacturafechaMesesFact(n, "opex", mesActual, ano) || primerDiaMes;
          mapa[mesActual] += convertirMontoCarteleriaPorFecha(d.monto, moneda, pais, fechaFactura);
        });
    }
  }

  return mapa;
};

/**
 * Calcula ingresos mensuales de GESTIÓN Y CONTENIDOS (facturación)
 * F3c: meses < 2026-10 → lógica histórica congelada; meses >= 2026-10 → devengo del motor (gestión y
 * contenidos; los contenidos solo dentro de su ventana `contenidos_fecha_inicio/fin`).
 * @param {number} ano - Año a calcular
 * @param {Array} negociosCarteleria - Array de negocios cartelería
 * @param {string} pais - "Chile" o "Perú"
 * @param {Array} costosGestionData - Datos de costos gestión (para fechas factura)
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
export const calcularGestionContenidosMensual = async (ano, negociosCarteleria, pais, costosGestionData = []) => {
  await loadExchangeRates();
  const hayHistorico = esMesHistorico(ano, 0);
  const hayMotor = !esMesHistorico(ano, 11);
  const historico = hayHistorico
    ? await calcularGestionContenidosMensualHistorico(ano, negociosCarteleria, pais, costosGestionData)
    : null;
  if (!hayMotor) return historico;
  const motor = calcularGestionContenidosMensualMotor(ano, negociosCarteleria, pais, costosGestionData, new Date());
  return historico ? combinarPorCorte(ano, historico, motor) : motor;
};

const calcularGestionContenidosMensualMotor = (ano, negociosCarteleria, pais, costosGestionData, hoy) => {
  const mapa = Array(12).fill(0);

  negociosCarteleria.forEach((negocio) => {
    if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
    if ((parseInt(negocio.plazo_contrato_meses) || 0) === 0) return;

    const cal = calendarioNegocio(negocio, { hoy });
    const devengoDe = (componente) => {
      const porMes = new Map();
      cal.devengo
        .filter((d) => d.componente === componente && d.ano === ano)
        .forEach((d) => porMes.set(d.mesNum - 1, d));
      return porMes;
    };
    const gestion = devengoDe("gestion");
    const contenidos = devengoDe("contenidos");

    for (let m = 0; m < 12; m++) {
      if (!gestion.has(m) && !contenidos.has(m)) continue;

      // Fecha para convertir UF/USD (misma prioridad que el código anterior).
      const registro = costosGestionData.find(
        (c) =>
          Number(c.negocio_id) === negocio.id &&
          Number(c.mes) === m + 1 &&
          Number(c.ano) === ano &&
          c.fecha_factura
      );
      const primerDiaMes = `${ano}-${String(m + 1).padStart(2, "0")}-01`;
      const fechaParaUF =
        registro?.fecha_factura?.split("T")[0] || getFacturafechaMesesFact(negocio, "gestion", m, ano) || primerDiaMes;

      let ingresoLocal = 0;
      if (gestion.has(m)) {
        ingresoLocal += convertirMontoCarteleriaPorFecha(
          gestion.get(m).monto,
          negocio.moneda_gestion,
          pais,
          fechaParaUF
        );
      }
      if (contenidos.has(m)) {
        // Moneda tal cual está cargada en el negocio (sin la inferencia del motor): igual que antes.
        ingresoLocal += convertirMontoCarteleriaPorFecha(
          contenidos.get(m).monto,
          negocio.moneda_contenidos,
          pais,
          fechaParaUF
        );
      }
      mapa[m] += ingresoLocal;
    }
  });

  return mapa;
};

/**
 * Calcula facturación total mensual (suma de todas las verticales)
 * @param {number} ano - Año a calcular
 * @param {Array} oportunidades - Oportunidades publicidad
 * @param {Array} salesBTL - Ventas BTL
 * @param {Array} negociosCarteleria - Negocios cartelería
 * @param {Array} costosGestionData - Datos costos gestión
 * @param {string} pais - "Chile" o "Perú"
 * @returns {Array<number>} Array(12) con facturación total por mes
 */
export const calcularFacturacionTotal = async (
  ano,
  oportunidades,
  salesBTL,
  negociosCarteleria,
  costosGestionData,
  pais
) => {
  const [publicidad, btl, hardware, gestion] = await Promise.all([
    calcularPublicidadMensual(ano, oportunidades, pais),
    calcularBTLMensual(ano, salesBTL, pais),
    calcularHardwareMensual(ano, negociosCarteleria, pais, costosGestionData),
    calcularGestionContenidosMensual(ano, negociosCarteleria, pais, costosGestionData),
  ]);

  return Array(12)
    .fill(0)
    .map((_, i) => (publicidad[i] || 0) + (btl[i] || 0) + (hardware[i] || 0) + (gestion[i] || 0));
};

/**
 * Helper: Calcular YTD (Year To Date)
 * @param {Array<number>} arrayMensual - Array con valores mensuales
 * @param {number} monthsToShow - Cantidad de meses a incluir
 * @returns {number} Suma acumulada
 */
export const calcularYTD = (arrayMensual, monthsToShow) =>
  arrayMensual.slice(0, monthsToShow).reduce((sum, val) => sum + val, 0);

// ============================================================================
// EXPORTACIÓN CONSOLIDADA
// ============================================================================

export default {
  // Conversión
  getUFValue,
  getDolarValue,
  convertirAMonedaLocal,

  // Legacy (compatibilidad)
  getConvertedImporte,
  getConvertedImporteBTL,
  normalizeMonth,
  calculateSalesSums,
  calculateForecast,
  sumarImportes,
  sumarImportesPorEstatus,

  // Cálculos mensuales
  calcularPublicidadMensual,
  calcularBTLMensual,
  calcularHardwareMensual,
  calcularGestionContenidosMensual,
  calcularFacturacionTotal,

  // Helpers
  calcularYTD,
};
